import React from "react";
import MetaData from "../../components/layout/MetaData";

const stackupSections = [
  {
    id: "pet-transparent",
    title: "PET Transparent Flexible PCB",
    summary:
      "PET transparent flexible boards are used for lightweight circuits with high transparency requirements. The stack-up is simple and usually focuses on copper, PET film and transparent cover protection.",
    note: "Use this option only when transparency is the main design target. Electrical and thermal capability should be confirmed before production.",
    examples: [
      {
        name: "Single-sided PET",
        layers: ["Transparent cover film", "Copper", "PET base film", "Adhesive"],
      },
      {
        name: "Double-sided PET",
        layers: ["Transparent cover film", "Copper", "PET base film", "Copper", "Transparent cover film"],
      },
    ],
  },
  {
    id: "thin-fpc",
    title: "Thin FPC",
    summary:
      "Thin FPC is suitable for compact electronic products where bending radius, total height and assembly space are restricted.",
    note: "The finished thickness shown in quote pages normally excludes stiffener and 3M adhesive tape.",
    examples: [
      {
        name: "Ultra-thin single layer",
        layers: ["PI coverlay", "Adhesive", "Copper", "PI base film"],
      },
      {
        name: "Thin double layer",
        layers: ["PI coverlay", "Copper", "PI dielectric", "Copper", "PI coverlay"],
      },
    ],
  },
  {
    id: "single-layer",
    title: "Single Layer FPC",
    summary:
      "Single layer FPC has one copper circuit layer and is commonly used for simple flexible interconnects, membrane keys and low-density wiring.",
    note: "It is the simplest FPC stack-up and is usually the first choice for cost-sensitive flexible circuits.",
    examples: [
      {
        name: "Single layer with coverlay",
        layers: ["PI coverlay", "Adhesive", "Copper", "PI base film"],
      },
      {
        name: "Single layer with stiffener",
        layers: ["PI coverlay", "Copper", "PI base film", "Stiffener", "3M tape"],
      },
    ],
  },
  {
    id: "two-layers",
    title: "2 Layers FPC",
    summary:
      "Two-layer FPC supports routing on both sides of the flexible dielectric. It is useful when one copper layer cannot complete the circuit routing.",
    note: "Via structure, copper balance and bending area should be reviewed together during DFM.",
    examples: [
      {
        name: "Standard 2-layer FPC",
        layers: ["PI coverlay", "Top copper", "PI dielectric", "Bottom copper", "PI coverlay"],
      },
      {
        name: "2-layer with adhesive",
        layers: ["PI coverlay", "Adhesive", "Top copper", "PI dielectric", "Bottom copper", "Adhesive", "PI coverlay"],
      },
    ],
  },
  {
    id: "four-layers",
    title: "4 Layers FPC",
    summary:
      "Four-layer FPC is used when higher routing density, shielding or controlled impedance is required in a flexible structure.",
    note: "For multilayer FPC, bending areas should avoid unnecessary copper and vias where possible.",
    examples: [
      {
        name: "4-layer flexible stack",
        layers: ["PI coverlay", "L1 copper", "PI dielectric", "L2 copper", "PI core", "L3 copper", "PI dielectric", "L4 copper", "PI coverlay"],
      },
    ],
  },
  {
    id: "rigid-flex",
    title: "Rigid-Flex Board",
    summary:
      "Rigid-flex boards combine rigid PCB areas with flexible connection areas, reducing connectors while improving assembly reliability.",
    note: "Rigid-flex projects need clear bend area, rigid area, stiffener and stack-up notes in fabrication files.",
    examples: [
      {
        name: "Rigid-flex reference",
        layers: ["Rigid solder mask", "Rigid copper", "FR-4 core", "Flexible copper", "PI flexible core", "Flexible copper", "FR-4 core", "Rigid copper", "Rigid solder mask"],
      },
    ],
  },
];

const materialRows = [
  ["PI", "Polyimide base film, common for flexible circuits and repeated bending."],
  ["PET", "Transparent or low-cost flexible base material for specific low-temperature applications."],
  ["Copper", "Circuit conductor layer. Thickness and width influence current capacity and bending behavior."],
  ["Coverlay", "Flexible protective film used instead of standard rigid PCB solder mask."],
  ["Stiffener", "Local reinforcement for connectors, soldering areas or assembly support."],
  ["3M tape", "Pressure-sensitive adhesive used for mounting or local fixture requirements."],
];

/**
 * 渲染单个 FPC 堆叠示意图。
 * @param {Object} props 组件属性
 * @param {string} props.name 堆叠名称
 * @param {string[]} props.layers 堆叠层级
 * @returns {JSX.Element} 堆叠示意图
 */
function StackupDiagram({ name, layers }) {
  return (
    <article className="fpc-stackup-example">
      <h4>{name}</h4>
      <div className="fpc-stackup-diagram" aria-label={`${name} stack-up diagram`}>
        {layers.map((layer, index) => (
          <div
            key={`${layer}-${index}`}
            className={`fpc-stackup-layer layer-${index % 5}`}
          >
            <span>{layer}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

/**
 * 渲染 FPC Stack-up 本地资料页。
 * @returns {JSX.Element} FPC Stack-up 页面
 */
export default function FpcStackupPage() {
  return (
    <>
      <MetaData title="Stack-up for FPC" />
      <div className="creatyea-padding fpc-stackup-page">
        <section className="fpc-stackup-hero">
          <div>
            <p className="fpc-stackup-eyebrow">PCB Prototype · Flexible PCB Guide</p>
            <h1>Stack-up for FPC</h1>
            <p>
              Local reference page for common FPC and rigid-flex stack-up structures.
              Use it together with the FPC/Rigid-Flex quotation options to select
              material, thickness and layer count more clearly.
            </p>
          </div>
          <a href="/get_quote" className="fpc-stackup-quote-link">
            Get FPC Quote
          </a>
        </section>

        <section className="fpc-stackup-reference">
          <h2>Material Reference</h2>
          <div className="fpc-stackup-material-grid">
            {materialRows.map(([name, description]) => (
              <div key={name} className="fpc-stackup-material-row">
                <strong>{name}</strong>
                <span>{description}</span>
              </div>
            ))}
          </div>
        </section>

        {stackupSections.map((section) => (
          <section key={section.id} id={section.id} className="fpc-stackup-section">
            <div className="fpc-stackup-section-copy">
              <h2>{section.title}</h2>
              <p>{section.summary}</p>
              <p className="fpc-stackup-note">{section.note}</p>
            </div>
            <div className="fpc-stackup-examples">
              {section.examples.map((example) => (
                <StackupDiagram
                  key={example.name}
                  name={example.name}
                  layers={example.layers}
                />
              ))}
            </div>
          </section>
        ))}

        <section className="fpc-stackup-footer-note">
          <h2>Design Notes</h2>
          <ul>
            <li>Keep dynamic bending areas free from unnecessary vias, pads and sharp copper corners.</li>
            <li>Mark stiffener, adhesive and exposed copper requirements clearly in Gerber or fabrication notes.</li>
            <li>For rigid-flex boards, provide rigid area, flexible area and bend direction drawings for review.</li>
            <li>When thickness, copper weight or impedance is critical, add the target stack-up in the fabrication file.</li>
          </ul>
        </section>
      </div>
    </>
  );
}
